/**
 * PaymentsFilters.tsx — the Payments section's filter panel (board AND list).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: admin payments (money board).
 *
 * WHY IT EXISTS NOW AND NOT BEFORE (2026-09-05). The board's own header carried
 * a standing instruction NOT to build this: "there is nothing on this wire to
 * filter ON… Do NOT add a Filters panel here whose only facet cannot reach the
 * server". That was accurate — `getPaymentsPipeline()` took no arguments and
 * `PaymentsPipelineCard` had no flag — and the same note named the server change
 * that would unblock it: "an `includeTest` query param plus `isTest` on the
 * card". orders-service has now shipped exactly that, so the facet reaches the
 * server and the panel follows. The instruction is honoured, not overruled.
 *
 * ⚠️ THE THIRD SCREEN, AND DELIBERATELY NOT A THIRD IMPLEMENTATION. The CRM
 * pipeline and the Patients directory already ask this question, in these words,
 * behind a "Filters (n)" button with a modal draft and a removable chip. Every
 * control in here comes from `ui/filters.tsx` and the frame is the estate's
 * `DialogShell`, so the money board learns the grammar an admin already knows
 * rather than inventing a third one — which is the whole reason those pieces
 * were lifted out of `pipeline/` in the first place.
 *
 * THE DRAFT IS THE POINT, as on both other screens: applying on every press
 * would fire a server read per click, and on THIS screen each read is a fresh
 * money aggregation plus a batched identity contact lookup over every visible
 * card. Apply commits once.
 *
 * BOTH VIEWS SINCE 2026-09-11. orders-service added the same `includeTest`
 * opt-in to the subscriptions and failed-payments reads, so the one applied
 * state now drives the List view as well as the board, and the hint below has
 * to be true of both.
 */
import { useState } from 'react';
import { DialogShell } from '../ui/ConfirmDialog';
import { FilterFooter, FilterSection, FilterTick, filterNoteStyle } from '../ui/filters';

export interface PaymentsFilterState {
  /** The opt-in audit view — sends orders' `includeTest=true` on the board read and on both list reads. */
  showTest: boolean;
}

export const EMPTY_PAYMENTS_FILTERS: PaymentsFilterState = { showTest: false };

/** How many distinct choices a state represents — the bar's badge count. */
export function activePaymentsFilterCount(f: PaymentsFilterState): number {
  return f.showTest ? 1 : 0;
}

/** The chip wording for an applied `showTest`, shared with the toolbar. */
export const PAYMENTS_TEST_CHIP = 'Test records shown';

export function PaymentsFilters({
  applied,
  onApply,
  onClose,
}: {
  applied: PaymentsFilterState;
  onApply: (next: PaymentsFilterState) => void;
  onClose: () => void;
}) {
  // Seeded from what is applied, so opening shows the current state and closing
  // without applying changes nothing.
  const [draft, setDraft] = useState<PaymentsFilterState>(applied);

  return (
    <DialogShell title="Filters" onCancel={onClose} width="min(720px, 100%)">
      {/* "Show test records" — THE PIPELINE'S LABEL, VERBATIM, under the same
          "Other" heading the Patients panel uses. One idea under three names on
          three staff screens is the defect the shared module exists to close, so
          the words are copied deliberately rather than re-written for the money
          board. */}
      <FilterSection label="Other" hint="Reporting hygiene, not patient work.">
        <FilterTick
          label="Show test records"
          describedBy="payments-filters-test-hint"
          checked={draft.showTest}
          onChange={() => setDraft((d) => ({ ...d, showTest: !d.showTest }))}
        />
        {/* ⚠️ THE HINT NAMES WHAT HAPPENS TO THE MONEY, because on this screen
            that is the consequence and it is not obvious. orders-service builds
            every rollup as Σ of the cards it returns, so switching this on adds
            the dry-run pence to the header figures as well as adding the cards —
            a header that disagreed with the cards under it would be unreadable,
            and "how much of this board is fake?" is meant to be a diff of the two
            views. An admin who reads a secured total in this view without
            knowing that would be reading rehearsal money as revenue. It names the
            lists as well as the board because the one control drives both views
            (2026-09-11). */}
        <p id="payments-filters-test-hint" style={{ ...filterNoteStyle, margin: '4px 0 0 30px' }}>
          Test records are hidden by default. Switching this on shows them on the board and in the lists,
          each marked Test, and adds their money to the board&rsquo;s totals &mdash; use it to audit, not to
          report.
        </p>
      </FilterSection>

      <FilterFooter onClear={() => setDraft(EMPTY_PAYMENTS_FILTERS)} onApply={() => onApply(draft)} />
    </DialogShell>
  );
}
